import { Query, FilterGroup, MeasurementName, RollupFunction } from '@kentik/ui-shared/query/query.types';
import { getTestFrequency } from '@kentik/ui-shared/synthetics/utils';
import {
  METRIC_TITLES,
  PING_METRICS,
  DNS_METRICS,
  HTTP_METRICS,
  PAGELOAD_METRICS,
  THROUGHPUT_METRICS
} from './constants';

export interface MetricChartConfig {
  key: string;
  title: string;
  query: Query;
}

/** The test surface the query builder reads. Excludes `results`, which only the UI needs. */
export interface TestConfigLike {
  id: number | string;
  isDns: boolean;
  isHttp: boolean;
  isPageLoad: boolean;
  isMesh: boolean;
  isAgentTest: boolean;
  isThroughputTest: boolean;
  isHostname: boolean;
  hasPing: boolean;
  get: (path: string, defaultValue?: unknown) => unknown;
}

export interface TestLike extends TestConfigLike {
  results: {
    actualStartDate: number;
    actualEndDate: number;
    sharedFilterAgentIds: number[];
    sharedFilterTargets: string[];
    sharedFilterLabelIds: string[];
    sharedFilterSiteIds: string[];
    sharedFilterAsns: string[];
    setSharedFilterAgentIds: (ids: number[]) => void;
    setSharedFilterTargets: (targets: string[]) => void;
    setSharedFilterLabelIds: (ids: string[]) => void;
    setSharedFilterSiteIds: (ids: string[]) => void;
    setSharedFilterAsns: (asns: string[]) => void;
    filters: { isActive: boolean };
    resetSharedFilters: () => void;
    pageTabId: string;
  };
}

export interface TimeRange {
  start: number;
  end: number;
}

export interface MetricFilterValues {
  agentIds: number[];
  targets: string[];
  labelIds: string[];
  siteIds: string[];
  asns: string[];
}

interface MetricDef {
  metric: string;
  measurement: MeasurementName;
  dimensions: string[];
  targetDimension?: string;
  /** Legend rollup fn; defaults to 'avg'. Status codes use 'max' (higher = worse). */
  rollupFn?: RollupFunction;
}

function estimateCardinality(test: TestConfigLike, dimensions: string[]): number {
  const agents = (test.get('config.agents') || []) as unknown[];
  const agentCount = agents.length;

  if (dimensions.length <= 1) {
    return agentCount;
  }

  // Multi-dimension: mesh/agent tests have agents × (agents-1) pairs
  if (test.isMesh || test.isAgentTest) {
    return agentCount * Math.max(agentCount - 1, 1);
  }

  // Target-based multi-dim: agents × targets
  const targetValue = test.isDns ? test.get('config.servers') : test.get('config.target.value');
  const targetCount = typeof targetValue === 'string' && targetValue ? targetValue.split(',').length : agentCount;
  return agentCount * targetCount;
}

export function getMetricDefs(test: TestConfigLike): MetricDef[] {
  const defs: MetricDef[] = [];

  if (test.isDns) {
    // DNS metric first
    for (const metric of DNS_METRICS) {
      if (metric === 'dns_latency_avg') {
        defs.push({
          metric,
          measurement: '/synthetics/dns',
          dimensions: ['src_agent', 'task_dns_resolver'],
          targetDimension: 'task_dns_resolver'
        });
      } else {
        defs.push({
          metric,
          measurement: '/synthetics/ping',
          dimensions: ['src_agent', 'dst_task_target'],
          targetDimension: 'dst_task_target'
        });
      }
    }
  } else if (test.isHttp) {
    for (const metric of HTTP_METRICS) {
      defs.push({
        metric,
        measurement: metric.startsWith('http_') ? '/synthetics/http' : '/synthetics/ping',
        dimensions: ['src_agent'],
        targetDimension: undefined,
        rollupFn: metric === 'http_status_code' ? 'max' : undefined
      });
    }
  } else if (test.isPageLoad) {
    for (const metric of PAGELOAD_METRICS) {
      defs.push({
        metric,
        measurement: metric.startsWith('pageload_') ? '/synthetics/pageload' : '/synthetics/ping',
        dimensions: ['src_agent'],
        targetDimension: undefined,
        rollupFn: metric === 'pageload_status_code' ? 'max' : undefined
      });
    }
  } else if (test.isThroughputTest) {
    const dims = test.isMesh || test.isAgentTest ? ['src_agent', 'dst_agent'] : ['src_agent', 'dst_task_target'];
    const targetDim = test.isMesh || test.isAgentTest ? 'dst_agent' : 'dst_task_target';
    for (const metric of THROUGHPUT_METRICS) {
      if (metric === 'throughput_bandwidth_avg') {
        defs.push({
          metric,
          measurement: '/synthetics/throughput',
          dimensions: dims,
          targetDimension: targetDim
        });
      } else {
        defs.push({
          metric,
          measurement: '/synthetics/ping',
          dimensions: dims,
          targetDimension: targetDim
        });
      }
    }
  } else if (test.hasPing) {
    // IP-address, network-grid, hostname, mesh, agent, flow-based tests
    let dims: string[];
    let targetDim: string | undefined;

    if (test.isMesh || test.isAgentTest) {
      dims = ['src_agent', 'dst_agent'];
      targetDim = 'dst_agent';
    } else if (test.isHostname) {
      // hostname tests have a single target, no need for dst dimension
      dims = ['src_agent'];
      targetDim = undefined;
    } else {
      dims = ['src_agent', 'dst_task_target'];
      targetDim = 'dst_task_target';
    }

    for (const metric of PING_METRICS) {
      defs.push({
        metric,
        measurement: '/synthetics/ping',
        dimensions: dims,
        targetDimension: targetDim
      });
    }
  }

  // The ping task is optional on http/dns/pageload/throughput tests — drop those panels when it's off,
  // otherwise they render as permanently empty charts.
  return test.hasPing ? defs : defs.filter((def) => def.measurement !== '/synthetics/ping');
}

function buildDimensionFilters(
  testId: string,
  agentIds: number[] = [],
  targets: string[] = [],
  labelIds: string[] = [],
  siteIds: string[] = [],
  asns: string[] = [],
  targetDimension?: string
): { connector: 'All'; filterGroups: FilterGroup[] } {
  const filterGroups: FilterGroup[] = [
    {
      connector: 'All',
      not: false,
      filters: [{ filterField: 'test_id', operator: '=', filterValue: testId, rightFilterField: '' }],
      filterGroups: []
    }
  ];

  if (agentIds.length > 0) {
    filterGroups.push({
      connector: 'Any',
      not: false,
      filters: agentIds.map((id) => ({
        filterField: 'src_agent',
        operator: '=',
        filterValue: String(id),
        rightFilterField: ''
      })),
      filterGroups: []
    });
  }

  if (labelIds.length > 0) {
    filterGroups.push({
      connector: 'Any',
      not: false,
      filters: labelIds.map((id) => ({
        filterField: 'ktlabel_syn_agent',
        operator: '=',
        filterValue: id,
        rightFilterField: ''
      })),
      filterGroups: []
    });
  }

  if (siteIds.length > 0) {
    filterGroups.push({
      connector: 'Any',
      not: false,
      filters: siteIds.map((id) => ({
        filterField: 'src_agent_site_id',
        operator: '=',
        filterValue: id,
        rightFilterField: ''
      })),
      filterGroups: []
    });
  }

  if (asns.length > 0) {
    filterGroups.push({
      connector: 'Any',
      not: false,
      filters: asns.map((asn) => ({
        filterField: 'src_agent_asn',
        operator: '=',
        filterValue: asn,
        rightFilterField: ''
      })),
      filterGroups: []
    });
  }

  if (targets.length > 0 && targetDimension) {
    filterGroups.push({
      connector: 'Any',
      not: false,
      filters: targets.map((t) => ({
        filterField: targetDimension,
        operator: '=',
        filterValue: t,
        rightFilterField: ''
      })),
      filterGroups: []
    });
  }

  return { connector: 'All', filterGroups };
}

export interface SynthHints {
  expectedCardinality?: number;
  testPeriod: number;
  maxPoints: number;
}

/**
 * Backend hints for full-size synth charts. `testPeriod` aligns output bins to the test frequency
 * (no null gaps) and `maxPoints` caps the bucket count. `expectedCardinality` drives two-pass and
 * i_sub_limit; omit it to keep the backend defaults.
 */
export function getSynthHints(test: TestConfigLike, expectedCardinality?: number): SynthHints {
  return {
    ...(expectedCardinality !== undefined && { expectedCardinality }),
    testPeriod: getTestFrequency(test) || 60,
    maxPoints: 288
  };
}

export function buildMetricCharts(
  test: TestConfigLike,
  timeRange: TimeRange,
  filters: MetricFilterValues,
  vizLimit = 25
): MetricChartConfig[] {
  const metricDefs = getMetricDefs(test);
  const testId = String(test.id);

  return metricDefs.map((def) => {
    const query: Query = {
      measurement: def.measurement,
      metrics: [{ name: def.metric }],
      dimensions: def.dimensions.map((name) => ({ name })),
      rollups: [{ metric: def.metric, fn: def.rollupFn || 'avg' }],
      filters: {
        dimensions: buildDimensionFilters(
          testId,
          filters.agentIds,
          filters.targets,
          filters.labelIds,
          filters.siteIds,
          filters.asns,
          def.targetDimension
        )
      },
      time: { lookback: 0, start: timeRange.start, end: timeRange.end },
      viz: { type: 'line', limit: vizLimit },
      limit: vizLimit
    };

    // Attach synth hints for backend query strategy decision (two-pass vs single-pass)
    Object.assign(query, {
      _synthHints: getSynthHints(test, estimateCardinality(test, def.dimensions))
    });

    return {
      key: `${def.measurement}__${def.metric}`,
      title: METRIC_TITLES[def.metric] || def.metric,
      query
    };
  });
}
